import type { ProgressBillingReport, ProgressBillingReportColumn, ProgressBillingReportKind } from './progressBilling';

export const PROGRESS_BILLING_COMPANY_NAME = "Leiper's Creek Timber Frames";
export const PROGRESS_BILLING_COMPANY_WEBSITE = 'www.lctimberframes.com';

export const PROGRESS_BILLING_PDF_METHOD_NOTE =
  'Percent complete is actual job cost less allocated overhead, divided by the proposed budget. Finalized jobs count as 100%. Earned revenue = contract × percent complete. Billed above earned is unearned revenue; billed below is under-billed.';

const PDF_HIDDEN_FIELDS = new Set(['group']);
/** WIP schedule printout stops at Earned. Billed, unearned/(under), and Est. GP stay on the KPI tiles. */
const WIP_PDF_HIDDEN_FIELDS = new Set(['billedToDate', 'overBilled', 'underBilled', 'estimatedGrossProfit']);

const PDF_LABELS: Record<string, string> = {
  project: 'Project',
  client: 'Client',
  contractValue: 'Contract',
  estimatedCost: 'Est. cost',
  costToDate: 'Cost to date',
  percentComplete: '% complete',
  earnedRevenue: 'Earned',
  billedToDate: 'Billed',
  netOverUnder: 'Unearned / (under)',
  estimatedGrossProfit: 'Est. GP',
  estimatedGrossProfitPct: 'GP %',
  completedAt: 'Completed',
  collectedToDate: 'Collected',
  openReceivable: 'Open A/R',
  remainingToBill: 'To bill',
  nextInstallment: 'Next installment',
  billingStyle: 'Billing',
  status: 'Status',
};

function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function longDate(date: Date): string {
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function progressBillingReportFilename(report: ProgressBillingReport, ext: 'csv' | 'pdf'): string {
  return `lctf-${report.kind.replace(/_/g, '-')}-${ymd(report.asOf)}.${ext}`;
}

export function progressBillingPdfTitle(report: ProgressBillingReport): string {
  return `${PROGRESS_BILLING_COMPANY_NAME} — ${report.title} (as of ${longDate(report.asOf)})`;
}

export function progressBillingCoverSubtitle(kind: ProgressBillingReportKind): string {
  if (kind === 'wip_schedule') return 'Cost-to-cost percent complete for under-contract jobs';
  if (kind === 'completed_projects') return 'Finalized under-contract jobs';
  return 'Billed, collected, and remaining to bill';
}

export function progressBillingPdfColumns(report: ProgressBillingReport): ProgressBillingReportColumn[] {
  return report.columns
    .filter(col => !PDF_HIDDEN_FIELDS.has(col.field))
    .filter(col => report.kind !== 'wip_schedule' || !WIP_PDF_HIDDEN_FIELDS.has(col.field))
    .map(col => ({ ...col, label: PDF_LABELS[col.field] || col.label }));
}

export function progressBillingPdfRows(
  report: ProgressBillingReport,
): Array<Record<string, string | number | Date | null>> {
  return report.rows.map(row => ({
    ...row,
    netOverUnder: Number(row.overBilled ?? 0) - Number(row.underBilled ?? 0),
  }));
}

export function progressBillingPdfTotals(report: ProgressBillingReport): Record<string, number> {
  return {
    ...report.totals,
    netOverUnder: (report.totals.overBilled || 0) - (report.totals.underBilled || 0),
  };
}

export function formatProgressBillingPdfCell(
  type: ProgressBillingReportColumn['type'],
  value: string | number | Date | null | undefined,
): string {
  if (value == null || value === '') return '—';
  if (type === 'currency') {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    const rounded = Math.round(n);
    const body = Math.abs(rounded).toLocaleString('en-US');
    return rounded < 0 ? `($${body})` : `$${body}`;
  }
  if (type === 'percentage') {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${n.toFixed(1)}%`;
  }
  if (type === 'date') {
    const d = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(d.getTime())) return '—';
    return longDate(d);
  }
  if (type === 'number') return String(value);
  return String(value);
}

export function progressBillingPdfKpis(report: ProgressBillingReport): Array<{ label: string; value: string }> {
  const t = report.totals;
  const money = (n: number | undefined) => formatProgressBillingPdfCell('currency', n ?? 0);
  if (report.kind === 'wip_schedule') {
    return [
      { label: 'Contract', value: money(t.contractValue) },
      { label: 'Cost to date', value: money(t.costToDate) },
      { label: 'Earned revenue', value: money(t.earnedRevenue) },
      { label: 'Billed to date', value: money(t.billedToDate) },
      { label: 'Unearned revenue', value: money(t.overBilled) },
      { label: 'Under-billed', value: money(t.underBilled) },
    ];
  }
  if (report.kind === 'completed_projects') {
    return [
      { label: 'Contract', value: money(t.contractValue) },
      { label: 'Final cost', value: money(t.costToDate) },
      { label: 'Gross profit', value: money(t.estimatedGrossProfit) },
      { label: 'Billed', value: money(t.billedToDate) },
      { label: 'Collected', value: money(t.collectedToDate) },
    ];
  }
  return [
    { label: 'Contract', value: money(t.contractValue) },
    { label: 'Billed', value: money(t.billedToDate) },
    { label: 'Collected', value: money(t.collectedToDate) },
    { label: 'Open A/R', value: money(t.openReceivable) },
    { label: 'Remaining to bill', value: money(t.remainingToBill) },
  ];
}

export function progressBillingPdfColumnFlex(field: string): number {
  if (field === 'project') return 2.4;
  if (field === 'client') return 1.5;
  if (field === 'nextInstallment') return 1.4;
  if (field === 'netOverUnder') return 1.35;
  if (field === 'status' || field === 'billingStyle') return 0.95;
  if (field === 'percentComplete' || field === 'estimatedGrossProfitPct') return 0.8;
  return 1;
}
